/**
 * RTK Query endpoints for the indexer platform (indexer service + repositories
 * service sources CRUD). Base paths after proxy + nginx rewrites:
 *  - `/indexer/graph`, `/indexer/search`  → indexer service (3850, DEV-002)
 *  - `/core-proxy/repositories/sources`   → repositories service (3700)
 *  - `/core-proxy/repositories/:id/sync`  → repositories service (3700)
 */
import { apiSlice } from './apiSlice';
import type {
  IndexerSource,
  IndexerSourceType,
  IndexerGraph,
  IndexerSearchRequest,
  IndexerSearchResponse,
  CommitSyncResult,
} from '@/types';

export interface IndexerSourceListParams {
  type?: IndexerSourceType;
}

export interface CreateIndexerSourceDto {
  collectionId?: string;
  mediaEnabled?: boolean;
}

export type UpdateIndexerSourceDto = Partial<CreateIndexerSourceDto> & {
  isActive?: boolean;
};

export const indexerApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ── Sources (repositories service, epic-3 §6) ─────────────────────────
    // NOTE: the repositories service does NOT wrap these responses in
    // { data: ... } — list/single/patch return bare documents/arrays.
    getIndexerSources: builder.query<IndexerSource[], IndexerSourceListParams | void>({
      query: (params) => ({
        url: '/core-proxy/repositories/sources',
        params: params || undefined,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'IndexerSource' as const, id: _id })),
              { type: 'IndexerSource', id: 'LIST' },
            ]
          : [{ type: 'IndexerSource', id: 'LIST' }],
    }),

    getIndexerSource: builder.query<IndexerSource, string>({
      query: (id) => `/core-proxy/repositories/sources/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'IndexerSource', id }],
    }),

    createIndexerSource: builder.mutation<IndexerSource, CreateIndexerSourceDto>({
      query: (body) => ({ url: '/core-proxy/repositories/sources', method: 'POST', body }),
      invalidatesTags: [{ type: 'IndexerSource', id: 'LIST' }],
    }),

    updateIndexerSource: builder.mutation<
      IndexerSource,
      { id: string; body: UpdateIndexerSourceDto }
    >({
      query: ({ id, body }) => ({
        url: `/core-proxy/repositories/sources/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, _error, { id }) =>
        result
          ? [{ type: 'IndexerSource', id }, { type: 'IndexerSource', id: 'LIST' }]
          : [{ type: 'IndexerSource', id: 'LIST' }],
    }),

    deleteIndexerSource: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({ url: `/core-proxy/repositories/sources/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'IndexerSource', id: 'LIST' }],
    }),

    /** POST /core-proxy/repositories/:id/sync — manual git source sync (GIT-4). */
    syncRepository: builder.mutation<CommitSyncResult, { id: string; branchName?: string }>({
      query: ({ id, branchName }) => ({
        url: `/core-proxy/repositories/${id}/sync`,
        method: 'POST',
        body: branchName ? { branchName } : {},
      }),
      invalidatesTags: [{ type: 'IndexerSource', id: 'LIST' }],
    }),

    // ── Graph (indexer service, IDX-5) ────────────────────────────────────
    getIndexerGraph: builder.query<IndexerGraph, void>({
      query: () => '/indexer/graph',
      providesTags: [{ type: 'IndexerGraph', id: 'LIST' }],
    }),

    // ── Unified search playground (indexer service, IDX-6 / UC-7) ─────────
    searchIndexer: builder.mutation<IndexerSearchResponse, IndexerSearchRequest>({
      query: (body) => ({ url: '/indexer/search', method: 'POST', body }),
      invalidatesTags: [{ type: 'IndexerSearch', id: 'LIST' }],
    }),
  }),
});

// Export auto-generated hooks.
export const {
  useGetIndexerSourcesQuery,
  useGetIndexerSourceQuery,
  useCreateIndexerSourceMutation,
  useUpdateIndexerSourceMutation,
  useDeleteIndexerSourceMutation,
  useSyncRepositoryMutation,
  useGetIndexerGraphQuery,
  useSearchIndexerMutation,
} = indexerApi;